import {
  BarChart3,
  Bot,
  Calendar,
  CheckCircle,
  Clock,
  Copy,
  Edit,
  Eye,
  FileText,
  Filter,
  MessageSquare,
  RefreshCw,
  Search,
  Send,
  Settings,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  XCircle,
} from "lucide-react";
import React from "react";
import { getStatusColors } from "../../../utils/function";
import WhatsappIcon from "../../../assets/images/whatsapp.png";
interface TemplatesProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  existingTemplates: any[];
  templateTypes: any[];
  samplePrompts: any[];
  filteredTemplates: any[];
  templateType: string;
  setTemplateType: (tab: string) => void;
  isGenerating: boolean;
  setIsGenerating: (value: boolean) => void;
  prompt: string;
  setPrompt: (value: string) => void;
  handleGenerate: () => void;
  handleFeedback: any;
  generatedTemplate: any;
  setGeneratedTemplate: (template: any) => void;
  handleRegenerate: () => void;
  feedback: string;
  setFeedback: (value: string) => void;
  showPreview: boolean;
  setShowPreview: (value: boolean) => void;
  copyTemplate: () => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  filterType: string;
  setFilterType: (value: string) => void;
  setSelectedTemplate: (template: any) => void;
}

const CreateTemplates: React.FC<TemplatesProps> = ({
  activeTab,
  existingTemplates,
  templateTypes,
  samplePrompts,
  filteredTemplates,
  templateType,
  setTemplateType,
  isGenerating,
  prompt,
  setPrompt,
  handleGenerate,
  handleFeedback,
  generatedTemplate,
  handleRegenerate,
  showPreview,
  setShowPreview,
  feedback,
  setFeedback,
  copyTemplate,
  searchTerm,
  setSearchTerm,
  filterType,
  setFilterType,
  setSelectedTemplate,
}) => {
  return (
    <>
      {activeTab === "create" ? (
        <div className="grid lg:grid-cols-1 gap-8">
          {/* Input Panel */}
          <div className="lg:col-span-1 space-y-6">
            {/* Template Type Selection */}
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-indigo-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Template Type
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {templateTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = templateType === type.id;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setTemplateType(type.id)}
                      className={`p-3 rounded-xl border-2 transition-all duration-200 ${
                        isSelected
                          ? `border-${type.color}-300 bg-${type.color}-50`
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 mx-auto mb-1 ${
                          isSelected
                            ? `text-${type.color}-600`
                            : "text-gray-500"
                        }`}
                      />
                      <span
                        className={`text-xs font-medium ${
                          isSelected
                            ? `text-${type.color}-700`
                            : "text-gray-600"
                        }`}
                      >
                        {type.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Prompt Input */}
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-indigo-100">
              <div className="flex items-center space-x-2 mb-4">
                <Sparkles className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Describe Your Template
                </h3>
              </div>

              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe what kind of WhatsApp template you need. Be specific about the purpose, tone, and any details you want included..."
                className="w-full h-32 p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-sm"
                disabled={isGenerating}
              />

              <div className="mt-4 space-y-3">
                <button
                  onClick={handleGenerate}
                  disabled={!prompt.trim() || isGenerating}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold py-3 rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isGenerating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>AI Generating...</span>
                    </>
                  ) : (
                    <>
                      <Bot className="w-5 h-5" />
                      <span>Generate Template</span>
                    </>
                  )}
                </button>

                {generatedTemplate && (
                  <button
                    onClick={handleRegenerate}
                    disabled={isGenerating}
                    className="w-full bg-gray-100 text-gray-700 font-medium py-3 rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Regenerate</span>
                  </button>
                )}
              </div>
            </div>

            {/* Sample Prompts */}
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-indigo-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Sample Prompts
              </h3>
              <div className="space-y-2">
                {samplePrompts.map((sample) => (
                  <button
                    key={sample.id}
                    onClick={() => setPrompt(sample.text)}
                    className="w-full text-left p-3 bg-gray-50 hover:bg-indigo-50 rounded-lg text-sm text-gray-700 hover:text-indigo-700 transition-colors border border-gray-100 hover:border-indigo-200"
                  >
                    {sample.text}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Generated Template Panel */}
          <div className="lg:col-span-2 space-y-6">
            {!generatedTemplate && !isGenerating && (
              <div className="bg-white rounded-2xl p-12 shadow-xl border border-indigo-100 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MessageSquare className="w-10 h-10 text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Ready to Create!
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  Describe your WhatsApp template needs and our AI will generate
                  a professional template for you in seconds.
                </p>
              </div>
            )}

            {isGenerating && (
              <div className="bg-white rounded-2xl p-12 shadow-xl border border-indigo-100 text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Bot className="w-10 h-10 text-white animate-pulse" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  AI is Working...
                </h3>
                <p className="text-gray-500 mb-6">
                  Analyzing your requirements and crafting the perfect template
                </p>
                <div className="flex justify-center space-x-1">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce"
                      style={{ animationDelay: `${i * 0.2}s` }}
                    />
                  ))}
                </div>
              </div>
            )}

            {generatedTemplate && (
              <div className="space-y-6">
                {/* Template Header */}
                <div className="bg-white rounded-2xl p-6 shadow-xl border border-indigo-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {generatedTemplate.name}
                        </h3>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                            {generatedTemplate.category}
                          </span>
                          <span className="text-sm text-gray-500">
                            Language: {generatedTemplate.language}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowPreview(!showPreview)}
                        className="p-2 bg-indigo-100 text-indigo-600 rounded-lg hover:bg-indigo-200 transition-colors"
                        title="Toggle Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={copyTemplate}
                        className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                        title="Copy Template"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Template Content */}
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Code View */}
                  <div className="bg-white rounded-2xl p-6 shadow-xl border border-indigo-100">
                    <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <Settings className="w-4 h-4" />
                      <span>Template Structure</span>
                    </h4>
                    <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
                      <pre className="text-sm text-green-400 whitespace-pre-wrap">
                        {JSON.stringify(generatedTemplate, null, 2)}
                      </pre>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="bg-white rounded-2xl p-6 shadow-xl border border-indigo-100">
                    <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <Eye className="w-4 h-4" />
                      <span>WhatsApp Preview</span>
                    </h4>
                    <div className="bg-gradient-to-b from-emerald-500 to-emerald-600 rounded-t-2xl p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center">
                          <img src={WhatsappIcon} alt="WhatsApp" />
                        </div>
                        <span className="text-white font-medium">
                          Your Business
                        </span>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-b-2xl border border-gray-200 space-y-3">
                      {generatedTemplate.header && (
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <div className="text-sm font-semibold text-gray-900">
                            {generatedTemplate.header.text?.replace(
                              /\{\{1\}\}/g,
                              "YourBrand"
                            )}
                          </div>
                        </div>
                      )}

                      <div className="bg-white p-3 rounded-lg shadow-sm">
                        <div className="text-sm text-gray-800 whitespace-pre-line">
                          {generatedTemplate.body.text
                            .replace(/\{\{1\}\}/g, "John")
                            .replace(/\{\{2\}\}/g, "#12345")
                            .replace(/\{\{3\}\}/g, "99.99")
                            .replace(/\{\{4\}\}/g, "2 items")
                            .replace(/\{\{5\}\}/g, "123 Main St, City")
                            .replace(/\{\{6\}\}/g, "TRK123456")
                            .replace(/\{\{7\}\}/g, "2-3 business days")}
                        </div>
                      </div>

                      {generatedTemplate.buttons &&
                        generatedTemplate.buttons.length > 0 && (
                          <div className="space-y-2">
                            {generatedTemplate.buttons.map(
                              (button: any, index: any) => (
                                <button
                                  key={index}
                                  className="w-full p-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
                                >
                                  {button.text}
                                </button>
                              )
                            )}
                          </div>
                        )}

                      {generatedTemplate.footer && (
                        <div className="text-xs text-gray-500 mt-2 pt-2 border-t">
                          {generatedTemplate.footer.text}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Feedback Section */}
                <div className="bg-white rounded-2xl p-6 shadow-xl border border-indigo-100">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    How does this look?
                  </h4>

                  <div className="flex items-center space-x-4 mb-4">
                    <button
                      onClick={() => handleFeedback("positive")}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 rounded-xl hover:bg-green-200 transition-colors"
                    >
                      <ThumbsUp className="w-4 h-4" />
                      <span>Looks Great!</span>
                    </button>
                    <button
                      onClick={() => handleFeedback("negative")}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-colors"
                    >
                      <ThumbsDown className="w-4 h-4" />
                      <span>Needs Work</span>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Any specific changes or improvements you'd like? The AI will use this feedback to regenerate the template..."
                      className="w-full h-20 p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none text-sm"
                    />

                    <div className="flex space-x-3">
                      <button
                        onClick={handleRegenerate}
                        disabled={isGenerating}
                        className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium py-3 rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 flex items-center justify-center space-x-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        <span>Improve Template</span>
                      </button>
                      <button className="px-6 bg-green-500 text-white font-medium py-3 rounded-xl hover:bg-green-600 transition-colors flex items-center space-x-2">
                        <Send className="w-4 h-4" />
                        <span>Use Template</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Existing Templates Tab */
        <div className="space-y-6">
          {/* Search and Filter Header */}
          <div className="bg-white rounded-2xl p-6 shadow-xl border border-indigo-100">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="relative flex-1 lg:w-80">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search templates..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div className="relative">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="appearance-none bg-white border border-gray-200 rounded-xl px-4 py-3 pr-8 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="all">All Types</option>
                    <option value="marketing">Marketing</option>
                    <option value="transactional">Transactional</option>
                    <option value="utility">Utility</option>
                    <option value="authentication">Authentication</option>
                  </select>
                  <Filter className="absolute right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  <span>
                    Approved:{" "}
                    {
                      existingTemplates.filter((t) => t.status === "APPROVED")
                        .length
                    }
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <span>
                    Pending:{" "}
                    {
                      existingTemplates.filter((t) => t.status === "PENDING")
                        .length
                    }
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <span>
                    Rejected:{" "}
                    {
                      existingTemplates.filter((t) => t.status === "REJECTED")
                        .length
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Templates Grid */}
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => {
              const statusColor = getStatusColors(template.status || "");
              const StatusIcon =
                template.status === "APPROVED"
                  ? CheckCircle
                  : template.status === "PENDING"
                  ? Clock
                  : XCircle;

              return (
                <div
                  key={template.id}
                  className="bg-white rounded-2xl p-6 shadow-xl border border-indigo-100 hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                  onClick={() => setSelectedTemplate(template)}
                >
                  {/* Template Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {template.name}
                        </h3>
                        {StatusIcon && (
                          <StatusIcon
                            className={`w-4 h-4 text-${statusColor}-500`}
                          />
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mb-2">
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-medium bg-${
                            statusColor === "green"
                              ? "emerald"
                              : statusColor === "yellow"
                              ? "amber"
                              : "red"
                          }-100 text-${
                            statusColor === "green"
                              ? "emerald"
                              : statusColor === "yellow"
                              ? "amber"
                              : "red"
                          }-700`}
                        >
                          {template.status}
                        </span>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                          {template.category}
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Template Preview */}
                  <div className="bg-gray-50 rounded-xl p-3 mb-4">
                    {template.header && (
                      <div className="text-sm font-semibold text-gray-900 mb-2">
                        {template.header.text?.replace(
                          /\{\{1\}\}/g,
                          "YourBrand"
                        )}
                      </div>
                    )}
                    <div className="text-sm text-gray-700 line-clamp-3">
                      {template.body.text
                        .replace(/\{\{1\}\}/g, "John")
                        .replace(/\{\{2\}\}/g, "#12345")
                        .replace(/\{\{3\}\}/g, "TRK123")}
                    </div>
                  </div>

                  {/* Template Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <BarChart3 className="w-4 h-4" />
                        <span>{template.usage?.toLocaleString()} sent</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {template.createdAt &&
                            new Date(template.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Eye className="w-4 h-4" />
                      <span>View</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {filteredTemplates.length === 0 && (
            <div className="bg-white rounded-2xl p-12 shadow-xl border border-indigo-100 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No templates found
              </h3>
              <p className="text-gray-500">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CreateTemplates;
